import { ASSETS_API_URL } from "config/api";
import React, { useCallback, useEffect, useState } from 'react'
import "./dashboard.scss";
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { ORDER_TYPES, TEXT_FIELDS, TIME_IN_FORCES, getApiErrorMessages } from "./pairTradingConfig";

const EMPTY_VALUE = "Not set";

const formatEnumLabel = (value) => String(value).replace(/_/g, " ");

// The API returns amounts with 18 decimal places, e.g. "100.000000000000000000".
// Trim trailing zeros on the string itself so no precision is lost.
const trimDecimal = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    if (!text.includes(".")) return text;
    return text.replace(/0+$/, "").replace(/\.$/, "");
};

const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const marketText = (name, symbol) => {
    if (name && symbol) return `${name} (${symbol})`;
    return name || symbol || EMPTY_VALUE;
};

// Same layout as the Add Pair page. Every setting is read only; the only
// thing that can change here is whether the pair is active.
const PairDetail = () => {

    const pairId = new URLSearchParams(useLocation().search).get('ids');
    const [pair, setPair] = useState(null);
    const [loading, setLoading] = useState(Boolean(pairId));
    const [loadError, setLoadError] = useState("");
    const [saving, setSaving] = useState(false);

    // quiet: refresh in the background without replacing the page with a
    // loading or error message.
    const loadPair = useCallback(async ({ quiet = false } = {}) => {
        if (!pairId) return;
        if (!quiet) {
            setLoading(true);
            setLoadError("");
        }
        try {
            const tok = localStorage.getItem("accessTokenDash");
            const response = await axios.get(`${ASSETS_API_URL}/pairs/${pairId}`, {
                headers: {
                    authorization: `Bearer ${tok}`
                },
            });
            const data = response?.data?.data;
            if (data) {
                setPair(data);
            } else if (!quiet) {
                setPair(null);
                setLoadError("Pair not found.");
            }
        } catch (error) {
            if (!quiet) {
                setPair(null);
                setLoadError(getApiErrorMessages(error).join(" "));
            }
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [pairId]);

    useEffect(() => {
        loadPair();
    }, [loadPair]);

    // PATCH /pairs/{id} only accepts { isActive }.
    const toggleActive = async () => {
        if (!pair || saving) return;
        const nextActive = !pair.isActive;
        setSaving(true);
        try {
            const tok = localStorage.getItem("accessTokenDash");
            const response = await axios.patch(
                `${ASSETS_API_URL}/pairs/${pair.id}`,
                { isActive: nextActive },
                {
                    headers: {
                        authorization: `Bearer ${tok}`
                    },
                }
            );
            const updated = response?.data?.data;
            setPair((prev) => (
                updated && typeof updated.isActive === "boolean"
                    ? { ...prev, ...updated }
                    : { ...prev, isActive: nextActive }
            ));
            toast.success(nextActive ? "Pair enabled" : "Pair disabled", {
                position: "top-right",
                autoClose: 3000,
            });
            loadPair({ quiet: true });
        } catch (error) {
            const messages = getApiErrorMessages(error);
            toast.error(
                <div>{messages.map((message, index) => <div key={index}>{message}</div>)}</div>,
                { position: "top-right", autoClose: 6000 }
            );
        } finally {
            setSaving(false);
        }
    };

    const renderShell = (children) => (
        <>
            <img className='elipse img-fluid' src='/assets/main-assets/Ellipse.png' alt="ellipse" />
            <div className="content">
                <div className="tokenicon pairdetailview">{children}</div>
            </div>
        </>
    );

    if (!pairId) {
        return renderShell(
            <div className="pairdetailmessage">
                <p>No pair selected.</p>
                <Link to="pairs">Back to pairs</Link>
            </div>
        );
    }

    if (loading) {
        return renderShell(
            <div className="pairdetailmessage">
                <p>Loading pair details...</p>
            </div>
        );
    }

    if (!pair) {
        return renderShell(
            <div className="pairdetailmessage">
                <p className="error">{loadError || "Pair not found."}</p>
                <div className='addmarketbuttonsss'>
                    <button type="button" onClick={() => loadPair()}>Retry</button>
                </div>
            </div>
        );
    }

    const config = pair.tradingConfig || {};
    const isActive = pair.isActive === true;
    const labelOf = (key) => TEXT_FIELDS[key].label;

    // Looks like the Add Pair market picker, but cannot be opened.
    const renderMarketBox = (key, name, symbol) => (
        <div className='shghsdhsdhgj drop'>
            <div className="dropdown buttons-list-all">
                <button className="sfwfqedsvdsfwqwef pairmarketbox" type="button" disabled>
                    <p className="dvhjbdhjefv" data-field={key}>{marketText(name, symbol)}</p>
                </button>
            </div>
        </div>
    );

    const renderField = (key, label, value, hint) => {
        const text = value === null || value === undefined || value === "" ? EMPTY_VALUE : String(value);
        return (
            <div className="pairfield" key={key}>
                <p>{label}</p>
                <input type="text" className="inputttclass" id={`pair-${key}`} data-field={key} value={text} readOnly />
                {hint && <p className="pairfieldhint">{hint}</p>}
            </div>
        );
    };

    // Shows every option, like Add Pair, with the pair's allowed ones highlighted.
    const renderChips = (key, label, options, values) => {
        const selectedValues = Array.isArray(values) ? values : [];
        const allOptions = options.concat(selectedValues.filter((value) => !options.includes(value)));
        return (
            <div className="pairfield pairfieldwide" key={key}>
                <p>{label}</p>
                <div className="pairchips" data-field={key}>
                    {allOptions.map((option) => (
                        <span key={option} className={`pairchip${selectedValues.includes(option) ? " selected" : ""}`}>
                            {formatEnumLabel(option)}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    let buttonText = isActive ? "Disable Pair" : "Enable Pair";
    if (saving) buttonText = isActive ? "Disabling..." : "Enabling...";

    return renderShell(
        <>
            <div className='parentheadingnetwok'>
                <p>Base Market</p>
                {renderMarketBox("baseMarket", pair.baseName, pair.base)}
                <p>Quote Market</p>
                <div className='drop'>
                    {renderMarketBox("quoteMarket", pair.quoteName, pair.quote)}
                </div>
            </div>

            <div className="pairformsection">
                <p className="pairsectiontitle">Trade Limits</p>
                <p className="pairsectionhint">Allowed order size. Notional is price multiplied by quantity.</p>
                <div className="pairfieldgrid maininputs">
                    {renderField("minTradeAmount", labelOf("minTradeAmount"), trimDecimal(config.minTradeAmount))}
                    {renderField("maxTradeAmount", labelOf("maxTradeAmount"), trimDecimal(config.maxTradeAmount))}
                    {renderField("minNotional", labelOf("minNotional"), trimDecimal(config.minNotional))}
                    {renderField("maxNotional", labelOf("maxNotional"), trimDecimal(config.maxNotional))}
                </div>
            </div>

            <div className="pairformsection">
                <p className="pairsectiontitle">Precision</p>
                <p className="pairsectionhint">How finely prices and quantities can be expressed.</p>
                <div className="pairfieldgrid maininputs">
                    {renderField("pricePrecision", labelOf("pricePrecision"), config.pricePrecision, "Decimal places allowed in a price.")}
                    {renderField("priceTickSize", labelOf("priceTickSize"), trimDecimal(config.priceTickSize), "Smallest allowed price step.")}
                    {renderField("quantityPrecision", labelOf("quantityPrecision"), config.quantityPrecision, "Decimal places allowed in a quantity.")}
                    {renderField("quantityStepSize", labelOf("quantityStepSize"), trimDecimal(config.quantityStepSize), "Smallest allowed quantity step.")}
                </div>
            </div>

            <div className="pairformsection">
                <p className="pairsectiontitle">Order Rules</p>
                <p className="pairsectionhint">Which orders traders may place on this pair.</p>
                <div className="pairfieldgrid maininputs">
                    {renderChips("allowedOrderTypes", "Allowed Order Types", ORDER_TYPES, config.allowedOrderTypes)}
                    {renderChips("allowedTimeInForces", "Allowed Time In Force", TIME_IN_FORCES, config.allowedTimeInForces)}
                </div>
            </div>

            <div className="pairformsection">
                <p className="pairsectiontitle">Risk Controls</p>
                <p className="pairsectionhint">Limits that protect traders from extreme prices.</p>
                <div className="pairfieldgrid maininputs">
                    {renderField("maxPriceDeviationBps", labelOf("maxPriceDeviationBps"), config.maxPriceDeviationBps, "100 bps = 1%.")}
                    {renderField("maxSlippageBps", labelOf("maxSlippageBps"), config.maxSlippageBps, "100 bps = 1%.")}
                    {renderField("referencePriceSource", "Reference Price Source", config.referencePriceSource && formatEnumLabel(config.referencePriceSource))}
                </div>
            </div>

            <div className="pairformsection">
                <p className="pairsectiontitle">Trading Schedule</p>
                <p className="pairsectionhint">When the pair opens and how it is billed.</p>
                <div className="pairfieldgrid maininputs">
                    {renderField("tradingStatus", "Trading Status", config.tradingStatus && formatEnumLabel(config.tradingStatus))}
                    {renderField("tradingStartsAt", labelOf("tradingStartsAt"), formatDateTime(config.tradingStartsAt), config.tradingStartsAt ? `Your local time. UTC: ${config.tradingStartsAt}` : null)}
                    {renderField("feeScheduleId", labelOf("feeScheduleId"), config.feeScheduleId)}
                </div>
            </div>

            <div className="pairformsection">
                <p className="pairsectiontitle">Pair Info</p>
                <div className="pairfieldgrid maininputs">
                    {renderField("pairSymbol", "Pair Symbol", pair.symbol)}
                    {renderField("pairId", "Pair ID", pair.id)}
                    {renderField("configVersion", "Config Version", config.configVersion)}
                    {renderField("createdAt", "Created At", formatDateTime(pair.createdAt))}
                    {renderField("updatedAt", "Last Updated", formatDateTime(pair.updatedAt))}
                    {pair.deletedAt && renderField("deletedAt", "Deleted At", formatDateTime(pair.deletedAt))}
                </div>
            </div>

            <div className="listedhead forwidthsss">
                <p className="availtab">Is Active</p>
                <label className={`switch${saving ? " is-saving" : ""}`}>
                    <input type="checkbox" checked={isActive} onChange={toggleActive} disabled={saving} aria-label="Is Active" />
                    <span className="slider round"></span>
                </label>
            </div>
            <div className='addmarketbuttonsss pairdetailactions'>
                <button type='button' onClick={toggleActive} disabled={saving}>{buttonText}</button>
            </div>
        </>
    );
}

export default PairDetail
